// The ingest boundary for every attribution value the takeover-app reads off
// a URL. One place, because we already shipped the failure it prevents.
//
// WHY THIS EXISTS. `meta_form_id` arrived as the literal string `{{form_id}}`
// on 1,565 of the 1,932 sessions created in the 14 days to 2026-09-09 — an
// unsubstituted ad-platform macro stored as if it were an id. It passed every
// `IS NOT NULL` check, so the column looked populated while naming nothing.
// The emitter (the Meta ad's destination URL, configured in Ads Manager) is
// fixed separately; fixing it is NOT sufficient, because a regression there,
// or any other template that leaks, silently refills the column with garbage
// that reads as data. So the boundary refuses the shape, here and on the API.
//
// AND IT NEVER RETURNS A BARE NULL. A null conflates "no param", "empty
// param", "placeholder" and "capture failed" into one indistinguishable
// absence — which is the same failure one level up: the unknown volume stays
// unknown. Every call returns a `reason`, so an unattributable session is
// COUNTABLE. See migration 233 and `app/services/attribution_guard.py`.

/** Reasons a value is absent. Kept in sync with the API guard + migration 233. */
export const REASONS = {
  CAPTURED:       'captured',        // a real value was read
  NO_PARAM:       'no_param',        // the param was not on the URL at all
  EMPTY:          'empty',           // present but empty / whitespace / a bare dash
  PLACEHOLDER:    'placeholder',     // an unsubstituted template: {{form_id}}, {x}, "null"
  INVALID:        'invalid',         // present, not a placeholder, but not a legal value
  CAPTURE_FAILED: 'capture_failed',  // storage threw (private browsing, disabled)
  NOT_APPLICABLE: 'not_applicable',  // not a funnel arrival at all (badge flow,
                                     // operator probe) — set server-side only
};

// An unsubstituted macro from any templating dialect: {{form_id}}, {form.id},
// {{ lead.leadgen_id }}. Meta, GHL and our own docs each use a different brace
// count, so match one-or-two of either.
const PLACEHOLDER_RE = /^\{\{?[^{}]*\}?\}$/;

// Literal stringified nothings. Same class as a brace macro: a template that
// interpolated the absence instead of the value.
const STRINGIFIED_NOTHING = new Set(['null', 'undefined', 'nil', 'none', 'nan']);

// A bare dash is how several ad tools render "not applicable".
const DASHES = new Set(['-', '--', '—']);

// Meta ids are numeric strings; a funnel tag is a slug someone types into a
// link. The same charset serves both, and anything else is a mangled link.
// Applied by DEFAULT — an unpatterned call was how `a b c` once passed.
// Mirrors ATTRIBUTION_VALUE_RE in `app/services/attribution_guard.py`.
export const DEFAULT_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,63}$/;

/**
 * Normalise one raw URL-param value into `{ value, reason }`.
 *
 * `value` is a real value or null — never a placeholder, never an empty
 * string. `reason` is always set, so the caller can store WHY it has nothing.
 *
 * @param {string|null|undefined} raw  the value as read from the URL
 * @param {RegExp} [pattern]           charset the value must match
 */
export function normaliseAttributionValue(raw, pattern = DEFAULT_PATTERN) {
  if (raw === null || raw === undefined) {
    return { value: null, reason: REASONS.NO_PARAM };
  }
  if (typeof raw !== 'string') {
    return { value: null, reason: REASONS.INVALID };
  }
  const trimmed = raw.trim();
  if (trimmed === '' || DASHES.has(trimmed)) {
    return { value: null, reason: REASONS.EMPTY };
  }
  if (PLACEHOLDER_RE.test(trimmed) || STRINGIFIED_NOTHING.has(trimmed.toLowerCase())) {
    return { value: null, reason: REASONS.PLACEHOLDER };
  }
  if (trimmed.length > 64) {
    return { value: null, reason: REASONS.INVALID };
  }
  if (pattern && !pattern.test(trimmed)) {
    return { value: null, reason: REASONS.INVALID };
  }
  return { value: trimmed, reason: REASONS.CAPTURED };
}

/** True when `raw` is a value we refuse to store. Convenience for callers
 *  that only need to filter (e.g. the Meta param sweep). */
export function isRejectedAttributionValue(raw) {
  return normaliseAttributionValue(raw).reason !== REASONS.CAPTURED;
}
